'use client';

import * as React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useDealerScorecardReport,
  type DealerScorecardReasonCount,
} from './useDealerScorecardReport';

export interface DealerScorecardReportProps {
  dateFrom: string;
  dateTo: string;
}

function RowSkeleton() {
  return (
    <div className="flex items-center gap-4 border-b border-border-primary p-3">
      <Skeleton className="h-4 w-48" />
      <Skeleton className="h-4 w-10" />
      <Skeleton className="h-4 w-10" />
      <Skeleton className="h-4 w-10" />
      <Skeleton className="h-4 w-14" />
      <Skeleton className="h-4 w-14" />
      <Skeleton className="h-4 w-32" />
    </div>
  );
}

function formatTat(seconds: number | null): string {
  if (seconds === null) return 'No conversions yet';
  return `${(seconds / 3600).toFixed(1)}h`;
}

function formatTopLossReasons(reasons: DealerScorecardReasonCount[]): string {
  if (reasons.length === 0) return 'No lost/cancelled requests';
  return reasons.map((reason) => `${reason.reason_label} (${reason.count})`).join(', ');
}

/** Per-dealer table -- submitted/quoted/converted counts, conversion rate, avg TAT, and top loss reasons -- driven entirely by useDealerScorecardReport, no hardcoded dealer list. */
export function DealerScorecardReport({ dateFrom, dateTo }: DealerScorecardReportProps) {
  const { data, isLoading, error } = useDealerScorecardReport(dateFrom, dateTo);

  if (isLoading) {
    return (
      <div aria-label="Loading dealer scorecard report">
        {Array.from({ length: 4 }).map((_, i) => (
          <RowSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-error-200 bg-error-50 p-4 text-sm text-error-700">
        Failed to load dealer scorecard report.
      </div>
    );
  }

  const rows = data ?? [];

  return (
    <div className="overflow-x-auto" data-testid="dealer-scorecard-table">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-border-primary text-left">
            <th className="p-3 font-medium">Dealer</th>
            <th className="p-3 font-medium">Submitted</th>
            <th className="p-3 font-medium">Quoted</th>
            <th className="p-3 font-medium">Converted</th>
            <th className="p-3 font-medium">Conversion %</th>
            <th className="p-3 font-medium">Avg TAT</th>
            <th className="p-3 font-medium">Top Loss Reasons</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.tenant_id} className="border-b border-border-primary">
              <td className="p-3 font-medium">{row.tenant_id}</td>
              <td className="p-3">{row.submitted_count}</td>
              <td className="p-3">{row.quoted_count}</td>
              <td className="p-3">{row.converted_count}</td>
              <td className="p-3">{row.conversion_rate_pct.toFixed(1)}%</td>
              <td className="p-3">{formatTat(row.avg_tat_seconds)}</td>
              <td className="p-3">{formatTopLossReasons(row.top_loss_reasons)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 ? (
        <p className="p-3 text-sm text-text-secondary">No dealer activity in this date range.</p>
      ) : null}
    </div>
  );
}
